import { posthog } from 'posthog-js';
import { assign, createActor, setup, type ActorRefFrom } from 'xstate';

import { subscribeProfile } from '@core/profile/store.js';
import { clientEnv } from '@core/types/env.js';
import { warnUnhandledEvent } from '@core/utils/xstate-utils.js';

import {
  identifyPostHog,
  initPostHog,
  markInternalOrTestUserPostHog,
  optInPostHog,
  optOutPostHog,
  resetPostHog,
} from './posthog.js';

type Capture = { event: string; data: Record<string, unknown> };

type PostHogContext = {
  unsubProfile: (() => void) | null;
  pending: Capture[];
};

type PostHogEvent =
  | { type: 'CONSENT_GRANTED' }
  | { type: 'CONSENT_REVOKED' }
  | { type: 'PROFILE_SET'; userId: string; isInternalOrTest: boolean }
  | { type: 'PROFILE_CLEAR' }
  | { type: 'TRACK'; event: string; data: Record<string, unknown> };

const PENDING_MAX = 100;

/**
 * PostHog tag lifecycle as an xstate v5 machine.
 *
 * - `idle` — SDK not loaded. `TRACK` queues into `context.pending` (bounded
 *   at 100, drops oldest on overflow). Consent grant transitions to
 *   `activeNoId` with `initPostHog` + queue drain + profile subscription.
 * - `activeNoId` — SDK loaded, opted in, no user identified yet. `TRACK`
 *   sends immediately. A `PROFILE_SET` transitions to `activeWithId`.
 * - `activeWithId` — same as above, identified. `PROFILE_CLEAR` resets and
 *   returns to `activeNoId`.
 * - `dormant` — SDK loaded but opted out (post-revoke). `TRACK` is dropped
 *   silently — no phantom queue across consent boundaries.
 *
 * The actor is exported as a module singleton so the PostHog tag wrapper
 * can send events without threading the ref through React. Tests reset via
 * `__resetPosthogMachineForTests()`.
 */
export const posthogMachine = setup({
  types: {
    context: {} as PostHogContext,
    events: {} as PostHogEvent,
  },
  actions: {
    init: () => initPostHog(clientEnv.VITE_POSTHOG_KEY),
    optIn: () => optInPostHog(),
    optOut: () => optOutPostHog(),
    reset: () => resetPostHog(),
    identify: (_, params: { userId: string; isInternalOrTest: boolean }) => {
      identifyPostHog(params.userId);
      markInternalOrTestUserPostHog(params.isInternalOrTest);
    },
    sendCapture: (_, params: { event: string; data: Record<string, unknown> }) =>
      posthog.capture(params.event, params.data),
    queueCapture: assign({
      pending: (
        { context },
        params: { event: string; data: Record<string, unknown> },
      ) => {
        const next = [...context.pending, { event: params.event, data: params.data }];
        while (next.length > PENDING_MAX) next.shift();
        return next;
      },
    }),
    flushPendingFromContext: assign({
      pending: ({ context }) => {
        for (const c of context.pending) posthog.capture(c.event, c.data);
        return [];
      },
    }),
    subscribeToProfile: assign({
      unsubProfile: ({ self }) =>
        subscribeProfile((p) => {
          self.send(
            p.userId
              ? {
                  type: 'PROFILE_SET',
                  userId: p.userId,
                  isInternalOrTest: p.isInternalOrTest,
                }
              : { type: 'PROFILE_CLEAR' },
          );
        }),
    }),
    unsubscribeFromProfile: assign({
      unsubProfile: ({ context }) => {
        context.unsubProfile?.();
        return null;
      },
    }),
  },
}).createMachine({
  id: 'posthog',
  initial: 'idle',
  context: { unsubProfile: null, pending: [] },
  on: {
    '*': {
      actions: ({ self, event }) => warnUnhandledEvent(self.id, event.type),
    },
  },
  states: {
    idle: {
      on: {
        CONSENT_GRANTED: {
          target: 'activeNoId',
          actions: ['init', 'flushPendingFromContext', 'subscribeToProfile'],
        },
        TRACK: {
          actions: {
            type: 'queueCapture',
            params: ({ event }) => ({ event: event.event, data: event.data }),
          },
        },
        // Idempotent no-ops — already opted out, no profile subscription yet.
        CONSENT_REVOKED: {},
        PROFILE_SET: {},
        PROFILE_CLEAR: {},
      },
    },
    activeNoId: {
      on: {
        PROFILE_SET: {
          target: 'activeWithId',
          actions: {
            type: 'identify',
            params: ({ event }) => ({
              userId: event.userId,
              isInternalOrTest: event.isInternalOrTest,
            }),
          },
        },
        CONSENT_REVOKED: {
          target: 'dormant',
          actions: ['optOut', 'reset', 'unsubscribeFromProfile'],
        },
        TRACK: {
          actions: {
            type: 'sendCapture',
            params: ({ event }) => ({ event: event.event, data: event.data }),
          },
        },
        // c15t fires both onConsentSet AND onConsentChanged on a first-time
        // save, so the tag forwards CONSENT_GRANTED twice; the second arrival
        // is a no-op here. Same for the profile subscription's initial emit
        // with userId=null while we're already in this no-id state.
        CONSENT_GRANTED: {},
        PROFILE_CLEAR: {},
      },
    },
    activeWithId: {
      on: {
        PROFILE_CLEAR: { target: 'activeNoId', actions: ['reset'] },
        CONSENT_REVOKED: {
          target: 'dormant',
          actions: ['optOut', 'reset', 'unsubscribeFromProfile'],
        },
        TRACK: {
          actions: {
            type: 'sendCapture',
            params: ({ event }) => ({ event: event.event, data: event.data }),
          },
        },
        // Same-user PROFILE_SET (re-emitted from the cache) is a no-op.
        // c15t re-firing CONSENT_GRANTED on subsequent saves is also a no-op.
        PROFILE_SET: {},
        CONSENT_GRANTED: {},
      },
    },
    dormant: {
      on: {
        CONSENT_GRANTED: {
          target: 'activeNoId',
          actions: ['optIn', 'subscribeToProfile'],
        },
        // Idempotent no-ops — already opted out, no profile subscription,
        // and TRACK drops silently across the consent boundary.
        CONSENT_REVOKED: {},
        PROFILE_SET: {},
        PROFILE_CLEAR: {},
        TRACK: {},
      },
    },
  },
});

let actor = createActor(posthogMachine, { id: 'posthog' });
actor.start();

export function posthogActorSend(event: PostHogEvent): void {
  actor.send(event);
}

export function getPosthogActor(): ActorRefFrom<typeof posthogMachine> {
  return actor;
}

/** Test-only: stop the running actor and create a fresh one. */
export function __resetPosthogMachineForTests(): void {
  actor.stop();
  actor = createActor(posthogMachine, { id: 'posthog' });
  actor.start();
}
